/**
 * Identity Service client using openapi-fetch
 */

import createClient from 'openapi-fetch'
import type { paths } from './generated/schema'
import { getAuthToken } from '../../lib/auth-provider'

// Type-safe client for the Identity Service
export type IdentityServiceClient = ReturnType<typeof createClient<paths>>

/**
 * Configuration options for the Identity Service client
 */
export interface BaseIdentityServiceConfig {
  baseUrl?: string
  headers?: HeadersInit
  credentials?: RequestCredentials
}
export interface IdentityServiceConfig extends BaseIdentityServiceConfig {
  serviceToken?: string
}

/**
 * Get default configuration for the Identity Service
 */
function getDefaultConfig(): Required<BaseIdentityServiceConfig> {
  // Check environment variables first, then fallback to defaults based on NODE_ENV
  const baseUrl =
    process.env.NEXT_PUBLIC_IDENTITY_SERVICE_URL ||
    process.env.IDENTITY_SERVICE_URL ||
    'https://identity.dev.aminochain.io'

  // Log warning if no env vars are set (only in development)
  if (
    process.env.NODE_ENV !== 'production' &&
    !process.env.NEXT_PUBLIC_IDENTITY_SERVICE_URL &&
    !process.env.IDENTITY_SERVICE_URL
  ) {
    console.warn('IDENTITY_SERVICE_URL not configured - using default:', baseUrl)
  }

  return {
    baseUrl,
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
  }
}

/**
 * Create a configured Identity Service client instance
 */
export function createIdentityServiceClient(config: IdentityServiceConfig = {}): IdentityServiceClient {
  const mergedConfig = { ...getDefaultConfig(), ...config }

  const client = createClient<paths>({
    baseUrl: mergedConfig.baseUrl,
    headers: mergedConfig.headers,
    credentials: mergedConfig.credentials,
  })

  // Add request interceptors for authentication
  client.use({
    async onRequest({ request }) {
      const token = mergedConfig.serviceToken || (await getAuthToken())
      if (token) request.headers.set('Authorization', `Bearer ${token}`)
      return request
    },

    async onResponse({ response }) {
      // Handle global response errors
      if (!response.ok) {
        console.error('Identity Service API request failed:', {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
        })
      }
      return response
    },
  })

  return client
}

// Create default client instance
export const identityServiceClient = createIdentityServiceClient()

// Export type-safe paths and components
export type { paths } from './generated/schema'
export type { components } from './generated/schema'
